import * as React from 'react'
import { useDispatch } from 'react-redux'
import { setIsStored } from '../../../redux/slices/Alerts'
import {
  Box,
  Paper,
  Grid,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Alert,
  Divider,
} from '@mui/material'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore'
import CheckIcon from '@mui/icons-material/Check'
import { datasetFormik } from './datasetFormik'
import { Formik, Form, useFormikContext } from 'formik'
import Textfield from '../../FormsUI/Textfield'
import Select from '../../FormsUI/Select'
import GeoJSONInput from '../../GeoJSONInput'
import Modal from '../../FormCreationModal'
import { createParroquia, updateParroquia, fetchParroquiaBoundary } from '../../../services/parroquiaService'
import { fetchCantonBoundary } from '../../../services/cantonService'

const STEPS = [
  { label: 'Información Básica', description: 'Datos de la Parroquia' },
  { label: 'Boundary GeoJSON', description: 'Límites geográficos' },
  { label: 'Centro', description: 'Coordenadas del centro' },
]

const gridStyle = {
  mt: 0,
  width: '100%',
  ml: 0,
  textAlignLast: 'right',
  justifyContent: 'space-evenly',
  pr: 2,
}

// Componente interno para el contenido del formulario
const FormContent = ({ formik, activeStep, setActiveStep, onCancel, onSubmit, initialValues }) => {
  const { values, errors, touched, setFieldValue, validateForm, setTouched } = useFormikContext()
  const [loadingBoundary, setLoadingBoundary] = React.useState(false)
  const [cantonReferenceGeoJSON, setCantonReferenceGeoJSON] = React.useState(null)

  // Obtener boundary del cantón seleccionado para mostrar como referencia en el mapa
  React.useEffect(() => {
    if (!values.id_canton || !formik.cantonesData?.length) return
    const canton = formik.cantonesData.find(c => String(c.id) === String(values.id_canton))
    if (!canton?.slug) return

    fetchCantonBoundary(canton.slug).then(geojson => {
      setCantonReferenceGeoJSON(geojson || null)
    })
  }, [values.id_canton, formik.cantonesData])

  // Cargar Boundary si es edición y no hay geojson
  React.useEffect(() => {
    const loadBoundary = async () => {
      // Si estamos en modo edición (hay initialValues con slug) y no hay geojson aún
      if (initialValues?.slug && !values.boundary_geojson && !loadingBoundary) {
        setLoadingBoundary(true)
        const geojson = await fetchParroquiaBoundary(initialValues.slug)
        if (geojson) {
          setFieldValue('boundary_geojson', JSON.stringify(geojson))
        }
        setLoadingBoundary(false)
      }
    }
    loadBoundary()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues])

  const otherPropsControl = {
    InputProps: { readOnly: false },
    variant: 'outlined',
  }

  const renderField = (field, index) => {
    if (field.isGeoJSONInput) {
      return (
        <Grid item xs={12} key={`field-${index}`}>
          <GeoJSONInput
            value={values.boundary_geojson}
            onChange={(val) => setFieldValue('boundary_geojson', val)}
            error={touched.boundary_geojson && errors.boundary_geojson}
            referenceGeoJSON={cantonReferenceGeoJSON}
          />
        </Grid>
      )
    }

    const child = !field.options ? (
      <Textfield
        {...otherPropsControl}
        {...field}
        type={field.type || 'text'}
      />
    ) : (
      <Select key={`field-${index}`} {...field} {...otherPropsControl} />
    )

    return (
      <Grid
        key={`field-${index}`}
        item
        xs={field.x}
        sx={{ textAlignLast: 'center' }}
      >
        {child}
      </Grid>
    )
  }

  const getStepFields = () => {
    switch (activeStep) {
      case 0:
        return formik.STEP1_FIELDS
      case 1:
        return formik.STEP2_FIELDS
      case 2:
        return formik.STEP3_FIELDS
      default:
        return []
    }
  }

  const getStepValidation = () => {
    switch (activeStep) {
      case 0:
        return formik.STEP1_VALIDATION
      case 1:
        return formik.STEP2_VALIDATION
      case 2:
        return formik.STEP3_VALIDATION
      default:
        return null
    }
  }

  const handleNext = async () => {
    const validation = getStepValidation()
    if (!validation) {
      setActiveStep(prev => prev + 1)
      return
    }

    // Validar solo los campos del paso actual
    const stepFields = getStepFields().map(f => f.name)
    const stepValues = {}
    stepFields.forEach(name => {
      stepValues[name] = values[name]
    })

    try {
      await validation.validate(stepValues, { abortEarly: false })
      setActiveStep(prev => prev + 1)
    } catch (err) {
      // Marcar campos como tocados para mostrar errores
      const touchedFields = {}
      stepFields.forEach(name => {
        touchedFields[name] = true
      })
      setTouched({ ...touched, ...touchedFields })

      // Validar el formulario completo para actualizar errores
      validateForm()
    }
  }

  const handleBack = () => {
    setActiveStep(prev => prev - 1)
  }

  const isLastStep = activeStep === STEPS.length - 1

  return (
    <Box>
      {/* Stepper Header */}
      <Stepper activeStep={activeStep} sx={{ pt: 2, pb: 3 }}>
        {STEPS.map((step) => (
          <Step key={step.label}>
            <StepLabel>
              <Typography variant="body2">{step.label}</Typography>
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      <Divider />

      {/* Step Description */}
      <Box sx={{ py: 2, px: 2, backgroundColor: 'grey.50' }}>
        <Typography variant="subtitle2" color="text.secondary">
          Paso {activeStep + 1}: {STEPS[activeStep].description}
        </Typography>
      </Box>

      <Divider />

      {/* Form Content */}
      <Paper
        style={{
          overflow: 'auto',
          borderRadius: 0,
          maxHeight: '50vh',
          minHeight: 300,
        }}
        sx={{ p: 2 }}
      >
        <Grid container spacing={2} sx={{ ...gridStyle }}>
          {getStepFields().map((field, index) => renderField(field, index))}
        </Grid>

        {/* Mensaje informativo en paso 3 */}
        {activeStep === 2 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Estos campos son opcionales. Si ingresas un GeoJSON válido en el paso anterior,
            se calcularán automáticamente.
          </Alert>
        )}
      </Paper>

      <Divider />

      {/* Navigation Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 2 }}>
        <Button
          onClick={onCancel}
          color="inherit"
        >
          Cancelar
        </Button>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
            startIcon={<NavigateBeforeIcon />}
          >
            Atrás
          </Button>

          {isLastStep ? (
            <Button
              variant="contained"
              color="primary"
              onClick={onSubmit}
              startIcon={<CheckIcon />}
            >
              {initialValues ? 'Actualizar' : 'Crear'}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleNext}
              endIcon={<NavigateNextIcon />}
            >
              Siguiente
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  )
}

const ParroquiaFormModal = (props, ref) => {
  const dispatch = useDispatch()

  const { initialValues, onClose, onSuccess } = props
  const [formik, setFormik] = React.useState(null)
  const [open, setOpen] = React.useState(false)
  const [activeStep, setActiveStep] = React.useState(0)
  const formikRef = React.useRef(null)

  const handleCancelAction = () => {
    setOpen(false)
    setActiveStep(0)
    if (onClose) onClose()
  }

  const handleSubmitForm = async () => {
    if (!formikRef.current) return
    formikRef.current.submitForm()
  }

  const handleAddAction = async values => {
    let resultSave = { stored: true }

    // Parsear boundary_geojson si es string y extraer solo la geometría
    let boundaryGeojson = undefined
    if (values.boundary_geojson) {
      try {
        let parsed = typeof values.boundary_geojson === 'string'
          ? JSON.parse(values.boundary_geojson)
          : values.boundary_geojson

        // Extraer la geometría según el tipo de GeoJSON
        if (parsed.type === 'FeatureCollection' && parsed.features?.length > 0) {
          // Si es FeatureCollection, extraer la geometría del primer feature
          boundaryGeojson = parsed.features[0].geometry
        } else if (parsed.type === 'Feature') {
          // Si es Feature, extraer solo la geometría
          boundaryGeojson = parsed.geometry
        } else if (parsed.type === 'Polygon' || parsed.type === 'MultiPolygon') {
          // Si ya es una geometría, usarla directamente
          boundaryGeojson = parsed
        } else {
          console.warn('Tipo de GeoJSON no soportado:', parsed.type)
          boundaryGeojson = parsed
        }
      } catch (e) {
        console.error('Error parsing boundary_geojson:', e)
      }
    }

    // Construir objeto base
    const data = {
      nombre: values.nombre,
      idCanton: parseInt(values.id_canton, 10),
      habilitado: Boolean(parseInt(values.habilitado, 10)),
      slug: values.slug || undefined,
      tipo: values.tipo || 'urbana',
      boundary_geojson: boundaryGeojson,
    }

    // Agregar campos opcionales solo si tienen valor
    if (values.center_lng !== '' && values.center_lng != null) {
      data.center_lng = parseFloat(values.center_lng)
    }
    if (values.center_lat !== '' && values.center_lat != null) {
      data.center_lat = parseFloat(values.center_lat)
    }

    let response
    if (initialValues?.id) {
      response = await updateParroquia(initialValues.id, data)
    } else {
      response = await createParroquia(data)
    }

    if (!response.success) {
      resultSave = { stored: false, message: response?.message || 'Error al guardar' }
    } else {
      if (onSuccess) onSuccess()
    }
    return resultSave
  }

  React.useImperativeHandle(ref, () => ({ openModal: () => setOpen(true) }))

  React.useEffect(() => {
    if (props.open !== undefined) setOpen(props.open)
  }, [props.open])

  React.useEffect(() => {
    if (!open) return
    datasetFormik().then(variables => {
      if (initialValues) {
        variables.INITIAL_FORMIK_STATE = {
          ...variables.INITIAL_FORMIK_STATE,
          nombre: initialValues.nombre || '',
          slug: initialValues.slug || '',
          id_canton: initialValues.idCanton || initialValues.id_canton || '',
          tipo: initialValues.tipo || 'urbana',
          habilitado: initialValues.habilitado === true ? 1 : 0,
          boundary_geojson: initialValues.boundary_geojson || '',
          center_lng: initialValues.center_lng ?? '',
          center_lat: initialValues.center_lat ?? '',
        }
      }
      setFormik(variables)
    })
    return () => {
      setFormik(null)
      setActiveStep(0)
    }
  }, [open, initialValues])

  if (!Boolean(formik)) return null

  return (
    <div>
      <Modal
        open={open}
        title={initialValues ? "Editar Parroquia" : "Crear una Parroquia"}
        description=""
        maxWidth="md"
      >
        <Box>
          <Formik
            innerRef={formikRef}
            initialValues={{ ...formik?.INITIAL_FORMIK_STATE }}
            validationSchema={formik?.FORM_VALIDATION}
            onSubmit={async values => {
              const itsStored = await handleAddAction(values)
              handleCancelAction()
              dispatch(setIsStored(itsStored))
            }}
          >
            <Form>
              <FormContent
                formik={formik}
                activeStep={activeStep}
                setActiveStep={setActiveStep}
                onCancel={handleCancelAction}
                onSubmit={handleSubmitForm}
                initialValues={initialValues}
              />
            </Form>
          </Formik>
        </Box>
      </Modal>
    </div>
  )
}

export default React.forwardRef(ParroquiaFormModal)
